import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import * as THREE from "three";
import { useDispatch, useSelector } from "react-redux";
import {
  deselectAllPVs,
  setloading,
  setTargetElementShow,
} from "../redux/sceneSlice";
import { deselectPVData } from "../redux/pvData";
import { deselectDaylight } from "../redux/daylightFactor";
import { setDefaultFloor } from "../redux/floorVisibility";
import { deselectEnergyData } from "../redux/roomData";
import { resetChosenFilter } from "../redux/sceneGlobal";
import { setDefault } from "../redux/sceneSlice";
import {
  deselectWindAlarmSensors,
  resetSensorPlacement,
} from "../redux/shadingSystem";
import { setDefaultVirtualSensors } from "../redux/solarSensors";
import { deselectWindSensors } from "../redux/windDirection";
import { hideSoundEmission } from "../redux/soundEmission";
import { resolveColorbarSettings } from "./utils";
import { HEATMAP_LABEL_LAYER } from "./threeDComponent/heatmapLabelLayers";
import { reduxLocalStorage } from "./utility/storage";
import axios from "axios";
import { arePolygonsSimilar } from "./utility/dataManipulation";
import { setWindowData } from "../redux/windowData";
import { transformToModelSpace } from "./utils";
import {
  isResourceInState,
  setIsSensorsDataCalled,
  setSensorsData,
} from "../redux/sensorAnalysis";
import { setManualMeshingSensors } from "../redux/sensorAnalysis";
import { addToast } from "../redux/toast";
import { useWindowSelection } from "./objectSelection";
import { publish, subscribe, unsubscribe, windowEvents } from "./events";
import { resources } from "../redux/sensorAnalysisConstants";
import { customApiCall } from "../utils/api";
import { colors } from "../utils/colors";
import { setPlacedObjects } from "../redux/objectPlacement";
import { useThree } from "@react-three/fiber";

export const useColorBar = (category, subcategry, dataMin, dataMax) => {
  const stored = useSelector(
    (state) => state.colorbars[category]?.[subcategry],
  );
  return resolveColorbarSettings(stored, { min: dataMin, max: dataMax });
};

export const useEnergyValueColorbarSettings = (valueKey) => {
  const minMaxDict = useSelector((state) => state.roomData.rangeValues);
  const colorbars = useSelector((state) => state.colorbars["Energy Data"]);
  return resolveColorbarSettings(colorbars?.[valueKey], minMaxDict?.[valueKey]);
};

export const useHandleWindowsSwitch = () => {
  const dispatch = useDispatch();
  var shadingDemandSelector = useSelector((state) => state.shadingSystem.value);
  var windRiskSelector = useSelector((state) => state.shadingSystem.value);
  const windAlarmSensors = useSelector(
    (state) => state.shadingSystem.windAlarmSensors,
  );
  useEffect(() => {
    const windriskIsSelected = windRiskSelector.checkStatus[0];
    const shadingProgressionIsSelected = shadingDemandSelector.checkStatus[2];
    const windAlarmIsSelected = Object.values(windAlarmSensors || {}).some(
      Boolean,
    );
    const isWindowVisible =
      !shadingProgressionIsSelected &&
      !windriskIsSelected &&
      !windAlarmIsSelected;
    dispatch(setTargetElementShow({ index: 4, value: isWindowVisible }));
  }, [
    shadingDemandSelector.checkStatus[2],
    windRiskSelector.checkStatus[0],
    windAlarmSensors,
  ]);
};

export const useHandlePVsSwitch = () => {
  const dispatch = useDispatch();
  const { pvFilter: pvPanelsFilter } = useSelector(
    (state) => state.sceneVisibility,
  );
  const { filter: pvDataFilter } = useSelector((state) => state.pvData);
  useEffect(() => {
    const pvDataIsSelected = Object.values(pvDataFilter)?.some((e) => e);
    if (pvDataIsSelected) dispatch(deselectAllPVs());
  }, [pvDataFilter]);

  useEffect(() => {
    const pvPanelsIsSelected = Object.values(pvPanelsFilter)?.some((e) => e);
    if (pvPanelsIsSelected) dispatch(deselectPVData());
  }, [pvPanelsFilter]);
};

export const useAllRoomsNames = () => {
  const data = useSelector((state) => state.roomData.api.data);
  const roomsNames = [];
  data?.forEach((floor) => {
    if (!Array.isArray(floor)) return;
    floor?.forEach((room) => {
      roomsNames.push(room.text);
    });
  });
  return roomsNames;
};

export const useUniqueRoomsFunctions = () => {
  const data = useSelector((state) => state.roomData.api.data);
  const roomsFunctions = [];
  data?.forEach((floor) => {
    if (!Array.isArray(floor)) return;
    floor?.forEach((room) => {
      if (!roomsFunctions.includes(room.room_function_value))
        roomsFunctions.push(room.room_function_value);
    });
  });
  return roomsFunctions;
};

export const useSensorPlacementColorBar = () => {
  var groupZone = useSelector((state) => state.shadingSystem.zoneGroup);
  var windRiskSelector = useSelector(
    (state) => state.shadingSystem.value.checkStatus,
  );
  const windAlarmSensors = useSelector(
    (state) => state.shadingSystem.windAlarmSensors,
  );

  const sensorCandidatesVisible =
    groupZone.some((e) => e) && windRiskSelector.slice(3).some((e) => e);
  const windAlarmSensorsVisible = Object.values(windAlarmSensors || {}).some(
    Boolean,
  );

  return {
    isVisible: sensorCandidatesVisible,
    sensorCandidatesVisible,
    windAlarmSensorsVisible,
    anySensorLayerVisible: sensorCandidatesVisible || windAlarmSensorsVisible,
  };
};

export const useActiveWindAlarmScenario = () => {
  const shadingControlScenarios =
    useSelector((state) => state.sceneVar.shadingControlScenarios) ?? [];
  const windAlarmByScenario = useSelector(
    (state) => state.shadingSystem.windAlarmSensors,
  );

  return (
    shadingControlScenarios.find(
      (scenario) => windAlarmByScenario?.[scenario],
    ) ??
    Object.entries(windAlarmByScenario || {}).find(([, on]) => on)?.[0] ??
    null
  );
};

export const useWindTurbineColorBar = () => {
  var windTurbine = useSelector((state) => state.windTurbine);
  try {
    var {
      value: { checkStatus: selectedZones },
      valuesRanges: {
        aep_kwh: [minValue, maxValue],
      },
    } = windTurbine;
    const isVisible = Object.values(selectedZones).some((e) => e);
    return { minValue, maxValue, isVisible };
  } catch {
    return { minValue: null, maxValue: null, isVisible: false };
  }
};

export const useSoundEmissionColorBar = () => {
  const { isVisible, valueRange } = useSelector(
    (state) => state.soundEmission,
  );
  return {
    isVisible,
    minValue: valueRange[0],
    maxValue: valueRange[1],
  };
};

export const useSolarSensorsColorBar = () => {
  var solarSensorSelector = useSelector((state) => state.sensorSolars);

  const visiblity = solarSensorSelector.value.checkStatus;
  const visibleIndex = visiblity.indexOf(true);

  const min = solarSensorSelector.rangeList[visibleIndex * 2];
  const max = solarSensorSelector.rangeList[visibleIndex * 2 + 1];
  const isVisible = visibleIndex !== -1;
  return { visibleIndex, min, max, isVisible };
};

export const useWindRiskColorBar = () => {
  const { max_time_velocity_more_than_12: maxTimeVelMoreThan12 } = useSelector(
    (state) => state.infoWindRisk,
  );
  var windRiskIsShowing = useSelector((state) => state.shadingSystem.value);
  const windRiskDataIsAvailable = useSelector(
    (state) => state.sceneVar.windRisk,
  );
  const isVisible = windRiskIsShowing.checkStatus[0] && windRiskDataIsAvailable;
  return { isVisible, maxTimeVelMoreThan12 };
};

export const useEnergyDataColorBar = () => {
  var colorBarVisibility = useSelector(
    (state) => state.roomData.roomEnergyVisibility,
  );
  const minMaxDict = useSelector((state) => state.roomData.rangeValues);
  const subcategory = useSelector((state) => state.roomData.currentValue);
  const haveRange = useSelector((state) => state.roomData.haveRange);

  var dataMin =
    minMaxDict?.[subcategory]?.min < 0 ? 0 : minMaxDict?.[subcategory]?.min;
  var dataMax = minMaxDict?.[subcategory]?.max;

  const selector = useSelector((state) => state.roomData.value);
  const isVisible =
    haveRange &&
    colorBarVisibility &&
    subcategory &&
    (selector?.parentName !== "") & (selector?.childName !== "");

  return { dataMax, dataMin, isVisible, subcategory };
};

export const useWindDirectionColorBar = () => {
  const {
    rangeList: [minValue, maxValue],
    value: { checkStatus: windSelector },
  } = useSelector((state) => state.windDirection);
  const isVisible = windSelector.some((e) => e);
  return { minValue, maxValue, isVisible };
};

export const useDaylightFactorColorBar = () => {
  var daylightSelector = useSelector((state) => state.daylightFactor.value);
  const subcategory = daylightSelector?.parentName;

  const isVisible =
    (daylightSelector?.parentName !== "") &
    (daylightSelector?.childName !== "");
  return { isVisible, subcategory };
};

export const usePVColorBar = () => {
  const { rangeValues, haveRange, unit, filter } = useSelector(
    (state) => state.pvData,
  );
  var maxVal = unit !== undefined ? rangeValues?.[unit]?.[1] : null;
  var minVal = 0;

  const selectedScenarios = Object.entries(filter)
    ?.filter(([scenario, isShown]) => isShown)
    ?.map(([scenario, isShown]) => scenario);
  const isVisible = haveRange && selectedScenarios?.length;
  return { isVisible, subcategory: unit, minVal, maxVal };
};

export const useWindAlarmColorBar = () => {
  const windAlarmSensors = useSelector(
    (state) => state.shadingSystem.windAlarmSensors,
  );
  const maxHoursWindAlarm = useSelector(
    (state) => state.windAlarmWindow.maxHoursWindAlarm,
  );
  const selectedMetric = useSelector(
    (state) => state.windAlarmWindow.selectedMetric,
  );
  const isVisible = Object.values(windAlarmSensors || {}).some(Boolean);
  return { isVisible, maxHoursWindAlarm, selectedMetric };
};

export const useIsAnyColorBarVisible = () => {
  const { isVisible: isWindRiskVisible } = useWindRiskColorBar();
  const { isVisible: isEnergyDataVisible } = useEnergyDataColorBar();
  const { isVisible: isSolarSensorsVisible } = useSolarSensorsColorBar();
  const { isVisible: isSensorPlacementVisible } = useSensorPlacementColorBar();
  const { isVisible: isWindAlarmVisible } = useWindAlarmColorBar();
  const { isVisible: isWindDirectionVisible } = useWindDirectionColorBar();
  const { isVisible: isDaylightFactorVisible } = useDaylightFactorColorBar();
  const { isVisible: isPVVisible } = usePVColorBar();
  const { isVisible: isWindTurbineVisible } = useWindTurbineColorBar();
  const { isVisible: isSoundEmissionVisible } = useSoundEmissionColorBar();

  return (
    isWindRiskVisible ||
    isEnergyDataVisible ||
    isSolarSensorsVisible ||
    isSensorPlacementVisible ||
    isWindAlarmVisible ||
    isWindDirectionVisible ||
    isDaylightFactorVisible ||
    isPVVisible ||
    isWindTurbineVisible ||
    isSoundEmissionVisible
  );
};

export const useHasHeatmapLabelLayer = () => {
  const { isVisible: isDaylightFactorVisible } = useDaylightFactorColorBar();
  const { isVisible: isEnergyDataVisible } = useEnergyDataColorBar();
  const { isVisible: isPVVisible } = usePVColorBar();
  const { isVisible: isWindRiskVisible } = useWindRiskColorBar();
  const { isVisible: isWindAlarmVisible } = useWindAlarmColorBar();
  const { isVisible: isSoundEmissionVisible } = useSoundEmissionColorBar();

  return (
    isDaylightFactorVisible ||
    isEnergyDataVisible ||
    isPVVisible ||
    isWindRiskVisible ||
    isWindAlarmVisible ||
    isSoundEmissionVisible
  );
};

export const useActiveHeatmapLabelLayer = () => {
  const { isVisible: isDaylightFactorVisible } = useDaylightFactorColorBar();
  const { isVisible: isEnergyDataVisible } = useEnergyDataColorBar();
  const { isVisible: isPVVisible } = usePVColorBar();
  const { isVisible: isWindRiskVisible } = useWindRiskColorBar();
  const { isVisible: isWindAlarmVisible } = useWindAlarmColorBar();
  const { isVisible: isSoundEmissionVisible } = useSoundEmissionColorBar();

  if (isDaylightFactorVisible) return HEATMAP_LABEL_LAYER.DAYLIGHT;
  if (isEnergyDataVisible) return HEATMAP_LABEL_LAYER.ENERGY;
  if (isPVVisible) return HEATMAP_LABEL_LAYER.PV;
  if (isWindRiskVisible) return HEATMAP_LABEL_LAYER.WIND_RISK;
  if (isWindAlarmVisible) return HEATMAP_LABEL_LAYER.WIND_ALARM;
  if (isSoundEmissionVisible) return HEATMAP_LABEL_LAYER.SOUND_EMISSION;
  return null;
};

export const useResetAllFilters = () => {
  var dispatch = useDispatch();

  const resetFilters = useCallback(() => {
    dispatch(deselectDaylight());
    dispatch(deselectEnergyData());
    dispatch(setDefault());
    dispatch(setDefaultFloor());
    dispatch(setDefaultVirtualSensors());
    dispatch(resetChosenFilter());
    dispatch(resetSensorPlacement());
    dispatch(deselectWindAlarmSensors());
    dispatch(deselectWindSensors());
    dispatch(deselectPVData());
    dispatch(hideSoundEmission());
  }, [dispatch]);

  return { resetFilters };
};

export const useHandleCallerAppMessages = () => {
  const [isMinified, setIsMinified] = useState(false);
  const { resetFilters } = useResetAllFilters();

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === "updateParams") {
        const params = event.data.params;
        const isMinified = Boolean(params?.is_minified);
        setIsMinified(isMinified);
        if (isMinified) resetFilters();
        window.parent.postMessage({ type: "paramsUpdated" }, "*");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [resetFilters]);

  return { isMinified };
};

export const useGetWindowData = () => {
  const project_id = reduxLocalStorage.getItem("project_id");
  const token = localStorage.getItem("token");
  const [resultArray, setResultArray] = useState([]);

  const dispatch = useDispatch();
  var floors = useSelector((state) => state.sceneVar.floorsList);

  let allURLs = useMemo(
    () =>
      floors?.map((eachFloor, i) => {
        return `${process.env.REACT_APP_API_URL}api/window_polygon/?project_id=${project_id}&floor=${eachFloor}`;
      }),
    [floors, project_id]
  );

  useEffect(() => {
    dispatch(setloading({ index: 4, value: true }));

    axios
      .all(
        allURLs.map((endpoint) =>
          axios.get(endpoint, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
        )
      )
      .then((data) => {
        dispatch(setloading({ index: 4, value: false }));
        setResultArray(data?.map((val) => val.data));
      })
      .catch(() => {
        dispatch(setloading({ index: 4, value: false }));
      });
  }, [allURLs, dispatch, token]);

  return { resultArray };
};

export const useGroupedWindowsData = () => {
  const { resultArray } = useGetWindowData();
  const dispatch = useDispatch();
  const [groupedWindows, setGroupedWindows] = useState([]);

  useEffect(() => {
    const groupedWindowsTemp = [];
    resultArray.forEach((floorWindows) => {
      floorWindows.forEach((window) => {
        const windowPolygon = new Float32Array(JSON.parse(window.polygon));
        let isSimilar = false;
        for (const group of groupedWindowsTemp) {
          const reperesentor = new Float32Array(JSON.parse(group[0].polygon));
          if (arePolygonsSimilar(reperesentor, windowPolygon)) {
            isSimilar = true;
            group.push(window);
            break;
          }
        }
        if (!isSimilar) groupedWindowsTemp.push([window]);
      });
    });
    setGroupedWindows(groupedWindowsTemp);

    if (resultArray.length > 0) {
      dispatch(
        setWindowData({ resultArray, groupedWindows: groupedWindowsTemp })
      );
    }
  }, [resultArray, dispatch]);
  return { groupedWindows, resultArray };
};

export const useManualMeshSensors = (externalValue) => {
  const sensors = useSelector(
    (state) => state.sensorAnalysis.manualMeshing.state.sensors
  );
  const dispatch = useDispatch();

  const setSensors = useCallback(
    (sensors) => dispatch(setManualMeshingSensors(sensors)),
    [dispatch]
  );

  useEffect(() => {
    setSensors(externalValue);
  }, [externalValue, setSensors]);

  return [sensors, setSensors];
};

export const useGetMeshSensorsData = () => {
  const { sensorsData, isSensorsDataFetched } = useSelector(
    (state) => state.sensorAnalysis
  );
  const { fetch } = useMeshSensorsCRUD();

  useEffect(() => {
    if (!isSensorsDataFetched) fetch();
  }, [fetch, isSensorsDataFetched]);

  return { sensors: sensorsData, fetch };
};

export const useMeshSensorsCRUD = () => {
  const project_id = reduxLocalStorage.getItem("project_id");
  const token = localStorage.getItem("token");
  const dispatch = useDispatch();

  const baseUrl = `${process.env.REACT_APP_API_URL_2}api/sensors`;
  const config = useMemo(
    () => ({
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  const fetch = useCallback(async () => {
    axios
      .get(
        `${baseUrl}?projectId=${project_id}&limit=300000&sensorTypes=solar&fields[]=id&fields[]=customerId&fields[]=utmX&fields[]=utmY&fields[]=utmZ&fields[]=name&fields[]=sensorType&fields[]=area&fields[]=cdif&fields[]=id&fields[]=projectId&fields[]=surfaceId`,
        config
      )
      .then((res) => {
        const sensors =
          res.data?.list?.map((item) => ({
            ...item,
            position: [item.utmX, item.utmY, item.utmZ],
          })) ?? [];

        const transformedSensors = sensors.map((sensor) => ({
          ...sensor,
          position: transformToModelSpace(sensor.position),
        }));
        dispatch(setSensorsData(transformedSensors));
      })
      .finally(() => {
        dispatch(setIsSensorsDataCalled(true));
      });
  }, [project_id, dispatch, config, baseUrl]);

  const thenFunction = (message) => {
    dispatch(
      addToast({
        message: message,
        type: "success",
      })
    );
  };
  const catchFunction = (message) => {
    dispatch(
      addToast({
        message: message,
        type: "error",
      })
    );
  };
  const finallyFunction = fetch;

  const remove = async (sensorsIds) => {
    await axios
      .all(
        sensorsIds.map((sensorId) =>
          axios.delete(`${baseUrl}/${sensorId}`, config)
        )
      )
      .then(() => thenFunction("Mesh removed successfully"))
      .catch(() => catchFunction("Failed to remove mesh"))
      .finally(finallyFunction);
  };

  const create = async (sensors) => {
    await axios
      .all(sensors.map((sensor) => axios.post(baseUrl, sensor, config)))
      .then(() => thenFunction("Mesh created successfully"))
      .catch(() => catchFunction("Failed to create mesh"))
      .finally(finallyFunction);
  };

  const updatePosition = async (sensors) => {
    await axios
      .all(
        sensors.map((sensor) => {
          const { utmX, utmY, utmZ } = sensor;
          const data = { utmX, utmY, utmZ };
          return axios.patch(`${baseUrl}/${sensor.id}`, data, config);
        })
      )
      .then(() => thenFunction("Mesh updated successfully"))
      .catch(() => catchFunction("Failed to update mesh"))
      .finally(finallyFunction);
  };

  return { remove, fetch, create, updatePosition };
};

export const useGetObjectPlacementData = () => {
  const { placedObjects, isPlacedObjectsFetched } = useSelector(
    (state) => state.objectPlacement
  );
  const { fetch } = useObjectPlacementCRUD();

  useEffect(() => {
    if (!isPlacedObjectsFetched) fetch();
  }, [fetch, isPlacedObjectsFetched]);

  return { placedObjects, fetch };
};

export const useObjectPlacementCRUD = () => {
  const baseUrl = `${process.env.REACT_APP_API_URL_2}api/objects`;
  const project_id = reduxLocalStorage.getItem("project_id");
  const dispatch = useDispatch();

  const fetch = useCallback(async () => {
    try {
      const url = `${baseUrl}?projectId=${project_id}&limit=300000`;
      const responseData = await customApiCall.get({ url });

      const transformedData = responseData?.list?.map((object) => ({
        ...object,
        position: transformToModelSpace(object.position),
      }));
      dispatch(setPlacedObjects(transformedData));
      return responseData;
    } catch (error) {
      console.log(error);
    }
  }, [project_id, baseUrl, dispatch]);

  const thenFunction = (message) => {
    dispatch(
      addToast({
        message,
        type: "success",
      })
    );
  };
  const catchFunction = (message) => {
    dispatch(
      addToast({
        message,
        type: "error",
      })
    );
  };
  const finallyFunction = fetch;

  const remove = async (objectId) => {
    try {
      await customApiCall.delete({ url: `${baseUrl}/${objectId}` });
      thenFunction("Object removed successfully");
    } catch (error) {
      catchFunction("Failed to remove object");
    } finally {
      finallyFunction();
    }
  };

  const create = async (object) => {
    try {
      await customApiCall.post({ url: baseUrl, data: object });
      thenFunction("Object created successfully");
    } catch (error) {
      catchFunction("Failed to remove object");
    } finally {
      finallyFunction();
    }
  };

  const updateObject = async (object) => {
    const { id, ...rest } = object;
    try {
      await customApiCall.patch({
        url: `${baseUrl}/${id}`,
        data: rest,
      });
      thenFunction("Object updated successfully");
    } catch (error) {
      catchFunction("Failed to update object");
    } finally {
      finallyFunction();
    }
  };

  return { remove, fetch, create, updateObject };
};

export const useClickWrapper = () => {
  const timeoutRef = useRef(null);
  const delayTime = 200;

  const singleClickWrapper = useCallback((handleSingleClick) => {
    return (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      timeoutRef.current = setTimeout(() => {
        handleSingleClick(...args);
        timeoutRef.current = null;
      }, delayTime);
    };
  }, []);

  const doubleClickWrapper = useCallback((handleDoubleClick) => {
    return (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      handleDoubleClick(...args);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        setTimeout(() => {
          clearTimeout(timeoutRef.current);
        }, delayTime);
      }
    };
  }, []);

  return { singleClickWrapper, doubleClickWrapper };
};

export const useGetSkylineData = ({ id, isVisible, type = "sensor" }) => {
  const [apiResult, setApiResult] = useState({ data: null, loading: null });
  const token = localStorage.getItem("token");
  const urlSection = type === "sensor" ? "sensors" : "surfaces";

  const callAPI = useCallback(
    async (objectId, token) => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL_2}api/${urlSection}/${objectId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    [urlSection]
  );

  const getSkylineData = useCallback(
    async (objectId) => {
      try {
        setApiResult({ data: null, loading: true });

        const response = await callAPI(objectId, token);
        const responseSkyline =
          type === "sensor" ? response.skyline : response.surfaceSkyline;

        if (response && responseSkyline) {
          let skyline = responseSkyline;

          let sensorSkyline;
          try {
            sensorSkyline = JSON.parse(skyline);
          } catch {
            sensorSkyline = {};
          }

          if (
            sensorSkyline?.fall_winter_skyline &&
            sensorSkyline?.spring_summer_skyline
          ) {
            sensorSkyline = sensorSkyline.spring_summer_skyline;
          }

          const heatmap = [];
          Object.values(sensorSkyline).forEach((dataY, x) => {
            dataY.forEach((data, y) => {
              if (!heatmap[y]) heatmap[y] = [];
              if (!heatmap[y][x]) heatmap[y][x] = 0;
              heatmap[y][x] += data;
            });
          });

          setApiResult({
            data: [{ z: heatmap, type: "heatmap" }],
            loading: false,
          });
        }
      } catch (error) {
      } finally {
        setApiResult((prev) => ({ ...prev, loading: false }));
      }
    },
    [token, callAPI, type]
  );

  useEffect(() => {
    if (id && isVisible) getSkylineData(id);
  }, [id, isVisible, getSkylineData]);

  return { apiResult };
};

export const getHasActiveFilters = (windowFilters) => {
  return (
    windowFilters?.floor?.length > 0 ||
    windowFilters?.name ||
    windowFilters?.shapeSize?.length > 0 ||
    windowFilters?.area?.length > 0
  );
};

export const useWindowPassFiltersCheck = () => {
  const windowFilters = useSelector((state) => state.windowData.filters);
  const sensoranalysisState = useSelector(
    (state) => state.sensorAnalysis.sensorAnalysisState
  );

  const isInSelectionMode = isResourceInState(
    sensoranalysisState,
    resources.WINDOW_FILTERS
  );

  const filteredWindow = useSelector(
    (state) => state.windowData.filteredWindows
  );

  const filteredWindowIds = useMemo(
    () => filteredWindow.map((w) => w.id),
    [filteredWindow]
  );

  const hasActiveFilters = getHasActiveFilters(windowFilters);
  const doesWindowPassFilters = useCallback(
    (windowId) => {
      if (
        isInSelectionMode &&
        hasActiveFilters &&
        !filteredWindowIds.includes(windowId)
      ) {
        return false;
      }
      return true;
    },
    [hasActiveFilters, filteredWindowIds, isInSelectionMode]
  );

  return { doesWindowPassFilters };
};

export const useSplitSensorsForMeshing = () => {
  const { sensors } = useGetMeshSensorsData();
  const { doesWindowPassFilters } = useWindowPassFiltersCheck();
  const sensoranalysisState = useSelector(
    (state) => state.sensorAnalysis.sensorAnalysisState
  );

  const isAdding = isResourceInState(
    sensoranalysisState,
    resources.IS_ADDING_MESH
  );
  const isMoving = isResourceInState(
    sensoranalysisState,
    resources.IS_MOVING_MESH
  );

  const { selectedWindow: baseSelectedWindow, selectedWindowsData } =
    useWindowSelection();

  const baseSelectedWindowSensors = useMemo(() => {
    return sensors?.filter(
      (sensor) =>
        sensor.surfaceId === baseSelectedWindow?.id &&
        doesWindowPassFilters(sensor?.surfaceId)
    );
  }, [sensors, baseSelectedWindow, doesWindowPassFilters]);

  const allSelectedWindowsSensors = useMemo(() => {
    return sensors?.filter(
      (sensor) =>
        selectedWindowsData?.some(
          (window) => window.id === sensor?.surfaceId
        ) && doesWindowPassFilters(sensor?.surfaceId)
    );
  }, [sensors, selectedWindowsData, doesWindowPassFilters]);

  const sensorsToAdding = useMemo(
    () => baseSelectedWindowSensors,
    [baseSelectedWindowSensors]
  );
  const sensorsToMoving = useMemo(
    () => allSelectedWindowsSensors,
    [allSelectedWindowsSensors]
  );

  const restOfSensors = useMemo(() => {
    return sensors?.filter((sensor) => {
      const goesToAdding = sensorsToAdding?.some((s) => s.id === sensor?.id);
      const goesToMoving = sensorsToMoving?.some((s) => s.id === sensor?.id);

      return (
        (!goesToAdding || !isAdding) &&
        (!goesToMoving || !isMoving) &&
        doesWindowPassFilters(sensor?.surfaceId)
      );
    });
  }, [
    sensors,
    sensorsToAdding,
    sensorsToMoving,
    doesWindowPassFilters,
    isAdding,
    isMoving,
  ]);

  return {
    sensorsToAdding,
    sensorsToMoving,
    restOfSensors,
  };
};

export const useEventSubscription = (name, fn, element) => {
  useEffect(() => {
    subscribe(name, fn, element);
    return () => {
      unsubscribe(name, fn, element);
    };
  }, [fn, name, element]);
};

export const useFetchData = (url, isActive) => {
  const [result, setResult] = useState([]);
  const [isCalled, setIsCalled] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await customApiCall.get({ url });
        setResult(response?.list ?? []);
      } catch (error) {
        console.error(error);
      } finally {
        setIsCalled(true);
      }
    };

    if (!isCalled && isActive) fetchData();
  }, [url, isCalled, isActive]);

  return { result };
};

function getAllRoofs(data) {
  const allRoofs = [];

  if (!data) return [];

  for (const building of data) {
    let buildingData;
    try {
      buildingData = JSON.parse(building.data.replace(/'/g, '"'));

      if (!buildingData?.length > 0) continue;
    } catch (e) {
      continue;
    }

    for (const surface of buildingData) {
      if (surface.surface_type === "ROOF") {
        allRoofs.push({ ...surface, id: `${building.id}_${surface?.ID}` });
      }
    }
  }

  return allRoofs;
}

export const useGetTargetBuildingRoofs = ({ isActive }) => {
  const project_id = reduxLocalStorage.getItem("project_id");
  const { result } = useFetchData(
    `${process.env.REACT_APP_API_URL_2}api/surroundings/buildings?projectId=${project_id}&target=true`,
    isActive
  );

  const roofs = useMemo(() => getAllRoofs(result ?? []), [result]);

  return { result: roofs };
};

export const useHoverObject = () => {
  const [hoveredObject, setHoveredObject] = useState(null);

  const onPointerEnter = (e, objectId) => {
    e?.stopPropagation();
    setHoveredObject(objectId);
    document.body.style.cursor = "pointer";
  };

  const onPointerLeave = (e) => {
    e?.stopPropagation();
    setHoveredObject(null);
    document.body.style.cursor = "default";
  };

  return { hoveredObject, onPointerEnter, onPointerLeave };
};

export const SELECTION_MODE = {
  SINGLE: "single",
  MULTIPLE: "multiple",
};

export const useSelectObject = ({
  selectionMode = SELECTION_MODE.SINGLE,
  defaultColor,
  externalSelectedObjects,
  setExternalSelectedObjects,
}) => {
  const { hoveredObject, onPointerEnter, onPointerLeave } = useHoverObject();
  const [internalSelectedObjects, setInternalSelectedObjects] = useState([]);

  const selectedObjects = useMemo(
    () => externalSelectedObjects ?? internalSelectedObjects,
    [internalSelectedObjects, externalSelectedObjects]
  );

  const setSelectedObjects = useCallback(
    (objects) => {
      if (setExternalSelectedObjects) {
        setExternalSelectedObjects(objects);
      } else {
        setInternalSelectedObjects(objects);
      }
    },
    [setExternalSelectedObjects]
  );

  const onSingleClick = useCallback(
    (e, objectId) => {
      e?.stopPropagation();
      if (selectionMode === SELECTION_MODE.SINGLE) {
        setSelectedObjects(selectedObjects === objectId ? null : objectId);
      } else if (selectionMode === SELECTION_MODE.MULTIPLE) {
        setSelectedObjects(
          selectedObjects.includes(objectId)
            ? selectedObjects.filter((id) => id !== objectId)
            : [...selectedObjects, objectId]
        );
      }
    },
    [selectionMode, setSelectedObjects, selectedObjects]
  );

  const getObjectColor = (objectId) => {
    const isSelected =
      selectionMode === SELECTION_MODE.SINGLE
        ? selectedObjects === objectId
        : selectedObjects?.includes(objectId);
    const isHovered = hoveredObject === objectId;

    let color;

    if (isSelected) {
      color = colors.selectedObject();
    } else if (isHovered) {
      color = colors.hoveredObject();
    } else {
      color = defaultColor ?? colors.defaultObject();
    }

    return new THREE.Color(color);
  };

  return {
    getObjectColor,
    onPointerEnter,
    onPointerLeave,
    onSingleClick,
  };
};

export const useActiveElements = () => {
  const { isPlacementMode } = useSelector((state) => state.polygonPlacement);
  const { isPlacementMode: isObjectPlacementMode } = useSelector(
    (state) => state.objectPlacement
  );

  return {
    windowClick: !isPlacementMode,
    roomClick: !isPlacementMode,
    objectPlacementClick: !isObjectPlacementMode,
  };
};

export const useOrbit = () => {
  const { gl } = useThree();
  const disableOrbit = useCallback(() => {
    publish(windowEvents.DISABLE_ORBIT, undefined, gl.domElement);
  }, [gl.domElement]);
  const enableOrbit = useCallback(() => {
    publish(windowEvents.ENABLE_ORBIT, undefined, gl.domElement);
  }, [gl.domElement]);

  const toggleOrbit = useCallback(() => {
    publish(windowEvents.TOGGLE_ORBIT, undefined, gl.domElement);
  }, [gl.domElement]);

  return { disableOrbit, enableOrbit, toggleOrbit };
};